'use client'

import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {ArrowButton} from '@/components/academy/ArrowButton'
import {options} from '@/components/academy/article/styledArticleOptions'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Text, TextVariant} from '@/components/common/content/Text'
import {Link} from '@/i18n'
import {theme} from '@/theme/theme'
import {AcademyContents, ContentfulAcademyArticle} from '@/utils/contentful'
import {withSequentialHeadingLevels} from '@/utils/contentful/headingLevels'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type AcademyArticleProps = {
  article: ContentfulAcademyArticle
  contents: AcademyContents
  breadcrumbTrail: BreadcrumbStep[]
}

const AcademyArticle: FC<React.PropsWithChildren<AcademyArticleProps>> = ({
  article,
  contents,
  breadcrumbTrail,
}) => {
  const articles = contents.flatMap((category) => category.articles)
  const index = articles.findIndex(({slug}) => slug === article.slug)
  const previous = index > 0 ? articles[index - 1] : undefined
  const next = index >= 0 ? articles[index + 1] : undefined

  return (
    <>
      <div className="breadcrumbs">
        <Breadcrumbs trail={breadcrumbTrail} hideCurrent />
      </div>
      <Text variant={TextVariant.h2} element="h1">
        {article.title}
      </Text>
      <article className="article">
        {documentToReactComponents(withSequentialHeadingLevels(article.text), options)}
      </article>
      <div className="navigation">
        {previous && (
          <Link href={routeTo.tmAcademy.article(previous.slug)} legacyBehavior>
            <a className="arrow">
              <ArrowButton variant="left" text={previous.title} />
            </a>
          </Link>
        )}
        {next && (
          <Link href={routeTo.tmAcademy.article(next.slug)} legacyBehavior>
            <a className="arrow next">
              <ArrowButton text={next.title} />
            </a>
          </Link>
        )}
      </div>

      <style jsx>{`
        .breadcrumbs {
          margin-bottom: ${theme.spacing[4]};
        }

        .article {
          margin-top: ${theme.spacing[5]};
        }

        .navigation {
          display: flex;
          justify-content: space-between;
          margin-top: ${theme.spacing[8]};
        }

        .arrow {
          max-width: 350px;
        }

        .arrow + .arrow {
          margin-left: ${theme.spacing[4]};
        }

        .arrow.next {
          margin-left: auto;
        }

        @media ${TABLET_SCREENSIZE} {
          .navigation {
            margin-top: ${theme.spacing[7]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .navigation {
            flex-direction: column-reverse;
            margin-top: ${theme.spacing[5]};
          }

          .arrow + .arrow {
            margin-left: auto;
            margin-bottom: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}

export default AcademyArticle
